import { UserEditFormData, UserFormData } from "@repo/types";
import { InfoIcon } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { Alert, AlertDescription, AlertTitle } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Form } from "../../../components/ui/form";
import {
  UserAssignmentSection,
  UserBasicSection,
  UserSettingsSection,
} from "./shared";

const FIELD_LABELS: Record<string, string> = {
  profil_id: "Profil",
  groupe_id: "Groupe",
  username: "Nom d'utilisateur",
  email_active: "Email vérifié",
  is_active: "Utilisateur actif",
  _form: "Formulaire",
};

function prettifyField(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

interface UserFormFieldsProps {
  form: UseFormReturn<UserFormData & { confirmPassword?: string }>;
  isEditMode: boolean;
  isSubmitting: boolean;
  error: string | null;
  fieldErrors: Record<string, string>;
  onSubmit: (data: UserEditFormData) => Promise<void>;
  onCancel: () => void;
}

export function UserFormFields({
  form,
  isEditMode,
  isSubmitting,
  error,
  fieldErrors,
  onSubmit,
  onCancel,
}: UserFormFieldsProps) {
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <Form {...form}>
        <div className="space-y-8">
          {error && (
            <Alert variant="destructive">
              <InfoIcon className="h-4 w-4" />
              <AlertTitle>Une erreur est survenue</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {hasFieldErrors && (
            <Alert variant="destructive">
              <InfoIcon className="h-4 w-4" />
              <AlertTitle>Erreurs de validation</AlertTitle>
              <AlertDescription>
                <ul className="mt-2 text-sm list-disc list-inside space-y-1">
                  {Object.entries(fieldErrors).map(([field, message]) => (
                    <li key={field}>
                      <span className="font-medium">{prettifyField(field)}</span>
                      : {message}
                    </li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <UserBasicSection
            form={form}
            isEditMode={isEditMode}
            fieldErrors={fieldErrors}
          />
          <UserAssignmentSection form={form} fieldErrors={fieldErrors} />
          <UserSettingsSection form={form} />

          <div className="flex items-center justify-between pt-6 border-t border-border">
            <Button type="button" variant="ghost" onClick={onCancel}>
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="min-w-36"
              onClick={form.handleSubmit(onSubmit)}
            >
              {isSubmitting ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Enregistrement...
                </div>
              ) : isEditMode ? (
                "Mettre à jour"
              ) : (
                "Créer l'utilisateur"
              )}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
}
